import { useState, useEffect, useContext } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, MessageSquare, User, Calendar, RefreshCw, ShieldAlert, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AuthContext } from "@/App";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminFeedbackPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/admin/feedback`, { withCredentials: true });
      setFeedbacks(response.data);
    } catch (error) {
      console.error("Error fetching feedback:", error);
      if (error.response?.status === 403) {
        setError("access_denied");
      } else {
        toast.error("Failed to load feedback");
        setError("fetch_error");
      }
    } finally {
      setLoading(false);
    }
  };

  const getPageColor = (page) => {
    const colors = {
      homepage: "bg-blue-100 text-blue-800",
      account: "bg-emerald-100 text-emerald-800",
      archive: "bg-purple-100 text-purple-800",
      general: "bg-slate-100 text-slate-800"
    };
    return colors[page] || colors.general;
  };

  // Access denied view
  if (error === "access_denied") {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/60 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_insurecloud-news/artifacts/g99eghuw_Owl%20Ai%20animated%20Gif.gif" 
                  alt="AI Newsletter Logo"
                  className="h-10 w-10"
                />
                <div>
                  <h1 className="text-xl font-bold text-slate-900 tracking-tight">AI Newsletter</h1>
                  <p className="text-xs text-slate-500">Powered by Owl Intelligence</p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/')}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </div>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 py-20">
          <Card className="shadow-lg border-red-200">
            <CardContent className="text-center py-16">
              <ShieldAlert className="h-20 w-20 mx-auto mb-6 text-red-400" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h2>
              <p className="text-slate-600 mb-6">
                This page is restricted to administrators only.
              </p>
              <Button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700">
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_insurecloud-news/artifacts/g99eghuw_Owl%20Ai%20animated%20Gif.gif" 
                alt="AI Newsletter Logo"
                className="h-10 w-10"
              />
              <div>
                <h1 className="text-xl font-bold text-slate-900 tracking-tight">AI Newsletter</h1>
                <p className="text-xs text-slate-500">Powered by Owl Intelligence</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="refresh-btn"
                onClick={fetchFeedback}
                variant="ghost"
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
              <Button
                data-testid="back-home-btn"
                onClick={() => navigate('/')}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Admin Badge */}
        <div className="mb-6">
          <Alert className="bg-purple-50 border-purple-200">
            <Shield className="h-5 w-5 text-purple-600" />
            <AlertDescription className="text-purple-900">
              <span className="font-semibold">Admin Panel</span> - User Feedback Management
            </AlertDescription>
          </Alert>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="shadow-sm border border-slate-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <MessageSquare className="h-10 w-10 text-blue-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-slate-900">{feedbacks.length}</p>
                <p className="text-sm text-slate-500">Total Feedback</p>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm border border-slate-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <User className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-slate-900">
                  {new Set(feedbacks.map(f => f.user_email)).size}
                </p>
                <p className="text-sm text-slate-500">Unique Users</p>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm border border-slate-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <Calendar className="h-10 w-10 text-purple-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-slate-900">
                  {feedbacks.filter(f => {
                    const today = new Date();
                    const feedbackDate = new Date(f.timestamp);
                    return feedbackDate.toDateString() === today.toDateString();
                  }).length}
                </p>
                <p className="text-sm text-slate-500">Today</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Feedback List */}
        {loading ? (
          <div className="text-center py-20">
            <RefreshCw className="h-10 w-10 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-slate-500">Loading feedback...</p>
          </div>
        ) : feedbacks.length === 0 ? (
          <Card className="shadow-sm border border-slate-200">
            <CardContent className="text-center py-20">
              <MessageSquare className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg text-slate-500">No feedback yet</p>
              <p className="text-sm text-slate-400 mt-2">User feedback will appear here</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {feedbacks.map((feedback) => (
              <Card key={feedback.id} className="shadow-sm border border-slate-200 hover:shadow-md transition-shadow" data-testid={`feedback-${feedback.id}`}>
                <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50/50 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge className={getPageColor(feedback.page)}>
                          {feedback.page}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {new Date(feedback.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <User className="h-4 w-4" />
                        <span className="font-medium">{feedback.user_email}</span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                    <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                      {feedback.feedback_text}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFeedbackPage;
